import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { FiX, FiArrowLeft, FiArrowRight, FiCheck } from 'react-icons/fi';

const PAD = 8;
const TOOLTIP_W = 300;

function TutorialTour({
  activo,
  pasos = [],
  onCerrar,
  onFinalizar,
  onCambioPaso,
  brandName = 'Agorax',
}) {
  const [indice, setIndice] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [tooltipH, setTooltipH] = useState(0);

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const h = tooltipRef.current.offsetHeight;
      setTooltipH((prev) => (Math.abs(prev - h) > 1 ? h : prev));
    }
  }, [indice, visible, rect]);

  const total = pasos.length;
  const paso = pasos[indice] || null;

  const localizar = useCallback((target, intentos = 0) => {
    if (!target) {
      setRect(null);
      setVisible(true);
      return;
    }
    const el = document.querySelector(target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      setVisible(true);
    } else if (intentos < 8) {
      timerRef.current = setTimeout(() => localizar(target, intentos + 1), 120);
    } else {
      setRect(null);
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (activo) {
      setIndice(0);
    } else {
      setVisible(false);
      setRect(null);
    }
  }, [activo]);

  useEffect(() => {
    if (!activo || !paso) return;
    setVisible(false);
    if (onCambioPaso) onCambioPaso(paso);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => localizar(paso.target), 380);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, indice]);

  useEffect(() => {
    if (!activo) return;
    const onResize = () => {
      if (paso) localizar(paso.target);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, indice]);

  useEffect(() => {
    if (!activo) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && onCerrar) onCerrar();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [activo, onCerrar]);

  if (!activo || !paso) return null;

  const cerrar = () => {
    setVisible(false);
    if (onCerrar) onCerrar();
  };
  const finalizar = () => {
    setVisible(false);
    if (onFinalizar) onFinalizar();
    else if (onCerrar) onCerrar();
  };
  const siguiente = () => {
    if (indice < total - 1) setIndice(indice + 1);
    else finalizar();
  };
  const anterior = () => {
    if (indice > 0) setIndice(indice - 1);
  };

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const esUltimo = indice === total - 1;

  let tooltipStyle;
  let highlight = null;

  if (rect && rect.width > 0) {
    const th = tooltipH || 300;
    const espacioAbajo = vh - (rect.top + rect.height);
    const espacioArriba = rect.top;
    const cabeAbajo = espacioAbajo >= th + PAD + 12;

    let top;
    if (cabeAbajo) {
      top = rect.top + rect.height + PAD + 6;
    } else if (espacioArriba >= th + PAD + 12) {
      top = rect.top - PAD - 6 - th;
    } else {
      top = (vh - th) / 2;
    }
    // Mantener el tooltip (y su encabezado) dentro de la pantalla
    if (top + th > vh - 12) top = vh - 12 - th;
    if (top < 12) top = 12;

    let left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
    if (left < 12) left = 12;
    if (left + TOOLTIP_W > vw - 12) left = vw - 12 - TOOLTIP_W;
    tooltipStyle = {
      ...S.tooltip,
      top,
      left,
      width: TOOLTIP_W,
      maxHeight: vh - 24,
      overflowY: 'auto',
    };
    highlight = (
      <div
        style={{
          position: 'fixed',
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          borderRadius: '14px',
          boxShadow: '0 0 0 9999px rgba(11,26,74,0.72)',
          border: '2px solid var(--color-surface)',
          zIndex: 5000,
          pointerEvents: 'none',
          animation: 'tutPulse 1.6s ease-in-out infinite',
        }}
      />
    );
  } else {
    tooltipStyle = {
      ...S.tooltip,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: Math.min(TOOLTIP_W, vw - 24),
    };
  }

  return (
    <div style={S.overlay}>
      {!highlight && <div style={S.fullDim} />}
      {highlight}
      <div
        ref={tooltipRef}
        style={{ ...tooltipStyle, opacity: visible ? 1 : 0 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={(paso.titulo || '').replace('{brand}', brandName)}
      >
        <div style={S.head}>
          <span style={S.contador}>
            Paso {indice + 1} de {total}
          </span>
          <button onClick={cerrar} style={S.closeBtn} aria-label="Cerrar">
            <FiX size={18} />
          </button>
        </div>
        <h3 style={S.titulo}>{(paso.titulo || '').replace('{brand}', brandName)}</h3>
        <p style={S.texto}>{paso.texto}</p>
        <div style={S.acciones}>
          <button onClick={cerrar} style={S.skipBtn}>
            Saltar
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {indice > 0 && (
              <button onClick={anterior} style={S.secBtn}>
                <FiArrowLeft size={15} /> Atras
              </button>
            )}
            <button onClick={siguiente} style={S.primBtn}>
              {esUltimo ? (
                <>
                  <FiCheck size={15} /> Empezar
                </>
              ) : (
                <>
                  Siguiente <FiArrowRight size={15} />
                </>
              )}
            </button>
          </div>
        </div>
        <div style={S.dots}>
          {pasos.map((_, i) => (
            <span key={i} style={{ ...S.dot, ...(i === indice ? S.dotActive : {}) }} />
          ))}
        </div>
      </div>
      <style>{`@keyframes tutPulse { 0%,100% { box-shadow: 0 0 0 9999px rgba(11,26,74,0.72), 0 0 0 0 rgba(255,255,255,0.4); } 50% { box-shadow: 0 0 0 9999px rgba(11,26,74,0.72), 0 0 0 6px rgba(255,255,255,0.15); } }`}</style>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 4999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  fullDim: { position: 'fixed', inset: 0, backgroundColor: 'rgba(11,26,74,0.72)', zIndex: 5000 },
  tooltip: {
    position: 'fixed',
    zIndex: 5001,
    backgroundColor: 'var(--color-surface)',
    borderRadius: '18px',
    padding: '18px',
    boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
    transition: 'opacity 0.25s ease',
    maxWidth: 'calc(100vw - 24px)',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  contador: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    padding: '2px',
    display: 'flex',
  },
  titulo: { margin: '0 0 6px', fontSize: '17px', fontWeight: 700, color: 'var(--color-primary)' },
  texto: {
    margin: '0 0 16px',
    fontSize: '14px',
    lineHeight: 1.5,
    color: 'var(--color-text-secondary)',
  },
  acciones: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  skipBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  secBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '9px 14px',
    borderRadius: '12px',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-secondary)',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  primBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '9px 16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  dots: { display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '14px' },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-border)',
    transition: 'all 0.2s ease',
  },
  dotActive: { width: '18px', borderRadius: '3px', backgroundColor: 'var(--color-secondary)' },
};

export default TutorialTour;
